@file:NoLiveLiterals

package com.example.borshi.web.referral

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.NoLiveLiterals
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.example.borshi.web.data.ReferralRepository
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.model.ReferralItem
import com.example.borshi.web.model.ReferralStatsData
import com.example.borshi.web.model.ReferralStatus
import com.example.borshi.web.styles.AppStyles
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

/**
 * Refer & Earn Screen - Main referral program UI (Step 4)
 * Shows user's referral code, stats, and history
 */
@Composable
fun ReferralScreen() {
    var loadingState by remember { mutableStateOf<LoadingState>(LoadingState.Loading) }
    var stats by remember { mutableStateOf<ReferralStatsData?>(null) }
    var history by remember { mutableStateOf<List<ReferralItem>>(emptyList()) }
    var copyFeedback by remember { mutableStateOf(false) }

    val repository = remember { ReferralRepository(FirebaseService) }
    val scope = rememberCoroutineScope()

    // Wait for Firebase auth to settle before loading data.
    // onAuthStateChanged fires immediately with the current user (or null),
    // so this avoids the race condition where currentUser is null on first render.
    DisposableEffect(Unit) {
        FirebaseService.init()
        val unsubscribe = FirebaseService.onAuthStateChanged { user ->
            if (user != null) {
                // User is signed in — load data
                scope.launch {
                    loadingState = LoadingState.Loading
                    // Step 1: Ensure the user has a referral code (idempotent cloud function call)
                    // This fixes users who paid via Cashfree but whose code was never generated
                    try {
                        FirebaseService.ensureReferralCode()
                    } catch (e: Throwable) {
                        console.warn("ensureReferralCode skipped:", e.message)
                    }
                    // Step 2: Load stats (code should now be present)
                    repository.getReferralStats().onSuccess { statsData ->
                        stats = statsData
                        repository.getReferralHistory().onSuccess { historyData ->
                            history = historyData
                            loadingState = LoadingState.Success
                        }.onFailure { error ->
                            console.error("Failed to load history:", error)
                            loadingState = LoadingState.Error("Failed to load referral history")
                        }
                    }.onFailure { error ->
                        console.error("Failed to load stats:", error)
                        loadingState = LoadingState.Error("Failed to load referral data")
                    }
                }
            } else {
                // Not signed in
                loadingState = LoadingState.Error("Please sign in to view your referrals")
            }
        }
        onDispose { unsubscribe() }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            padding(24.px)
            gap(24.px)
            maxWidth(800.px)
            property("margin", "0 auto")
        }
    }) {
        // Header
        H1({
            style {
                color(Color(AppStyles.primaryColor))
                fontSize(32.px)
                property("margin", "0")
            }
        }) {
            Text("Refer & Earn")
        }

        P({
            style {
                color(Color("#666"))
                fontSize(16.px)
                property("margin", "0")
            }
        }) {
            Text("Share your code and earn ₹100 when your friend makes their first purchase!")
        }

        // Content based on loading state
        when (loadingState) {
            LoadingState.Loading -> LoadingView()
            is LoadingState.Error -> ErrorView((loadingState as LoadingState.Error).message)
            LoadingState.Success -> {
                stats?.let { statsData ->
                    // Check if user has a referral code
                    if (statsData.referralCode.isEmpty() || statsData.referralCode.isBlank()) {
                        // No referral code - show empty state with retry
                        NoReferralCodeView(onRetry = {
                            scope.launch {
                                loadingState = LoadingState.Loading
                                try { FirebaseService.ensureReferralCode() } catch (e: Throwable) {
                                    console.warn("ensureReferralCode:", e.message)
                                }
                                repository.getReferralStats().onSuccess { newStats ->
                                    stats = newStats
                                    loadingState = LoadingState.Success
                                }.onFailure {
                                    loadingState = LoadingState.Success
                                }
                            }
                        })
                    } else {
                        // Has referral code - show normal content
                        // Referral Code Card
                        ReferralCodeCard(
                            code = statsData.referralCode,
                            onCopy = {
                                copyToClipboard(statsData.referralCode)
                                copyFeedback = true
                                scope.launch {
                                    kotlinx.coroutines.delay(2000)
                                    copyFeedback = false
                                }
                            },
                            onShare = {
                                shareReferralCode(statsData.referralCode)
                            },
                            showCopyFeedback = copyFeedback
                        )

                        // Stats Cards
                        StatsCards(statsData)

                        // Referral History
                        ReferralHistory(history)
                    }
                } ?: run {
                    // No stats data at all
                    NoReferralCodeView(onRetry = {
                        scope.launch {
                            loadingState = LoadingState.Loading
                            try { FirebaseService.ensureReferralCode() } catch (e: Throwable) {
                                console.warn("ensureReferralCode:", e.message)
                            }
                            repository.getReferralStats().onSuccess { newStats ->
                                stats = newStats
                                loadingState = LoadingState.Success
                            }.onFailure {
                                loadingState = LoadingState.Success
                            }
                        }
                    })
                }
            }
        }
    }
}

/**
 * Loading state for the screen
 */
sealed class LoadingState {
    object Loading : LoadingState()
    object Success : LoadingState()
    data class Error(val message: String) : LoadingState()
}

/**
 * Loading view with spinner
 */
@Composable
fun LoadingView() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                property("animation", "spin 1s linear infinite")
            }
        }) {
            Text("⏳")
        }

        // Add CSS animation
        Style {
            Text("""
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            """.trimIndent())
        }
    }
}

/**
 * Error view
 */
@Composable
fun ErrorView(message: String) {
    Div({
        style {
            padding(24.px)
            backgroundColor(Color("#FEE"))
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Color("#F88"))
        }
    }) {
        P({
            style {
                color(Color("#C00"))
                property("margin", "0")
            }
        }) {
            Text("⚠️ $message")
        }
    }
}

/**
 * No referral code view - shows when user doesn't have a code yet
 */
@Composable
fun NoReferralCodeView(onRetry: () -> Unit = {}) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(24.px)
            alignItems(AlignItems.Center)
            padding(48.px, 24.px)
        }
    }) {
        // Icon
        Div({
            style {
                fontSize(64.px)
            }
        }) {
            Text("🎁")
        }

        // Title
        H2({
            style {
                color(Color(AppStyles.primaryColor))
                fontSize(24.px)
                property("margin", "0")
                textAlign("center")
            }
        }) {
            Text("Start Referring & Earn!")
        }

        // Main message - Make first purchase
        Div({
            style {
                padding(20.px)
                backgroundColor(Color("#FFF8E1"))
                borderRadius(12.px)
                border(2.px, LineStyle.Solid, Color("#FFB300"))
                maxWidth(500.px)
                width(100.percent)
            }
        }) {
            P({
                style {
                    color(Color("#F57C00"))
                    fontSize(16.px)
                    fontWeight("600")
                    property("margin", "0 0 12px 0")
                    textAlign("center")
                }
            }) {
                Text("🛒 Make Your First Purchase")
            }

            P({
                style {
                    color(Color("#666"))
                    fontSize(14.px)
                    property("margin", "0")
                    textAlign("center")
                    lineHeight("1.6")
                }
            }) {
                Text("Complete your first order to unlock your unique referral code and start earning ₹100 for every friend you refer!")
            }
        }

        // Secondary message - Contact support or retry
        Div({
            style {
                padding(20.px)
                backgroundColor(Color("#E3F2FD"))
                borderRadius(12.px)
                border(1.px, LineStyle.Solid, Color("#2196F3"))
                maxWidth(500.px)
                width(100.percent)
            }
        }) {
            P({
                style {
                    color(Color("#1976D2"))
                    fontSize(14.px)
                    fontWeight("600")
                    property("margin", "0 0 8px 0")
                    textAlign("center")
                }
            }) {
                Text("💬 Already Completed a Purchase?")
            }

            P({
                style {
                    color(Color("#666"))
                    fontSize(14.px)
                    property("margin", "0 0 12px 0")
                    textAlign("center")
                    lineHeight("1.6")
                }
            }) {
                Text("If you've already paid and don't see your code, tap the button below to generate it now.")
            }

            // Retry button - re-runs the cloud function to assign code
            Button({
                style {
                    display(DisplayStyle.Block)
                    property("margin", "0 auto")
                    padding(10.px, 24.px)
                    backgroundColor(Color("#1976D2"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(8.px)
                    fontSize(14.px)
                    fontWeight("600")
                    cursor("pointer")
                }
                onClick { onRetry() }
            }) {
                Text("🔄 Generate My Referral Code")
            }
        }

        // How it works section
        Div({
            style {
                marginTop(24.px)
                padding(24.px)
                backgroundColor(Color("#F5F5F5"))
                borderRadius(12.px)
                maxWidth(500.px)
                width(100.percent)
            }
        }) {
            P({
                style {
                    color(Color(AppStyles.primaryColor))
                    fontSize(16.px)
                    fontWeight("600")
                    property("margin", "0 0 16px 0")
                    textAlign("center")
                }
            }) {
                Text("How It Works")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                }
            }) {
                // Step 1
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        alignItems(AlignItems.FlexStart)
                    }
                }) {
                    Span({
                        style {
                            fontSize(20.px)
                            minWidth(30.px)
                        }
                    }) {
                        Text("1️⃣")
                    }
                    P({
                        style {
                            property("margin", "0")
                            fontSize(14.px)
                            color(Color("#666"))
                        }
                    }) {
                        Text("Make your first purchase and get your unique referral code")
                    }
                }

                // Step 2
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        alignItems(AlignItems.FlexStart)
                    }
                }) {
                    Span({
                        style {
                            fontSize(20.px)
                            minWidth(30.px)
                        }
                    }) {
                        Text("2️⃣")
                    }
                    P({
                        style {
                            property("margin", "0")
                            fontSize(14.px)
                            color(Color("#666"))
                        }
                    }) {
                        Text("Share your code with friends")
                    }
                }

                // Step 3
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        alignItems(AlignItems.FlexStart)
                    }
                }) {
                    Span({
                        style {
                            fontSize(20.px)
                            minWidth(30.px)
                        }
                    }) {
                        Text("3️⃣")
                    }
                    P({
                        style {
                            property("margin", "0")
                            fontSize(14.px)
                            color(Color("#666"))
                        }
                    }) {
                        Text("When they make their first purchase, you both get ₹100!")
                    }
                }
            }
        }
    }
}

/**
 * Referral code card with copy and share buttons
 */
@Composable
fun ReferralCodeCard(
    code: String,
    onCopy: () -> Unit,
    onShare: () -> Unit,
    showCopyFeedback: Boolean
) {
    Div({
        style {
            padding(24.px)
            backgroundColor(Color("#F5F5F5"))
            borderRadius(12.px)
            border(2.px, LineStyle.Dashed, Color(AppStyles.primaryColor))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
            alignItems(AlignItems.Center)
        }
    }) {
        P({
            style {
                fontSize(14.px)
                color(Color("#666"))
                property("margin", "0")
                fontWeight("bold")
            }
        }) {
            Text("YOUR REFERRAL CODE")
        }

        // Large code display
        Div({
            style {
                fontSize(36.px)
                fontWeight("bold")
                color(Color(AppStyles.primaryColor))
                letterSpacing(4.px)
                fontFamily("monospace")
                property("user-select", "all")
            }
        }) {
            Text(code.ifEmpty { "Loading..." })
        }

        // Buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                flexWrap(FlexWrap.Wrap)
                justifyContent(JustifyContent.Center)
            }
        }) {
            // Copy button
            Button({
                onClick { onCopy() }
                style {
                    backgroundColor(Color(AppStyles.primaryColor))
                    color(Color.white)
                    padding(12.px, 24.px)
                    borderRadius(8.px)
                    border(0.px)
                    cursor("pointer")
                    fontSize(16.px)
                    fontWeight("600")
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    alignItems(AlignItems.Center)
                }
            }) {
                Text(if (showCopyFeedback) "✓ Copied!" else "📋 Copy Code")
            }

            // Share button
            Button({
                onClick { onShare() }
                style {
                    backgroundColor(Color(AppStyles.primaryColor))
                    color(Color.white)
                    padding(12.px, 24.px)
                    borderRadius(8.px)
                    border(0.px)
                    cursor("pointer")
                    fontSize(16.px)
                    fontWeight("600")
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    alignItems(AlignItems.Center)
                }
            }) {
                Text("🔗 Share Code")
            }
        }

        // How it works
        Div({
            style {
                fontSize(12.px)
                color(Color("#666"))
                textAlign("center")
                maxWidth(400.px)
            }
        }) {
            Text("Share this code with friends. You both get ₹100 when they make their first purchase!")
        }
    }
}

/**
 * Statistics cards showing referral metrics
 */
@Composable
fun StatsCards(stats: ReferralStatsData) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(16.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        // Total referrals
        StatCard(
            title = "Total Referrals",
            value = stats.totalReferrals.toString(),
            icon = "👥",
            color = "#4CAF50"
        )

        // Total bonus earned
        StatCard(
            title = "Total Earned",
            value = "₹${stats.totalBonus.toInt()}",
            icon = "💰",
            color = "#FF9800"
        )

        // Pending
        StatCard(
            title = "Pending",
            value = stats.pendingReferrals.toString(),
            icon = "⏳",
            color = "#2196F3"
        )

        // Completed
        StatCard(
            title = "Completed",
            value = stats.completedReferrals.toString(),
            icon = "✓",
            color = "#8BC34A"
        )
    }
}

/**
 * Individual stat card
 */
@Composable
fun StatCard(title: String, value: String, icon: String, color: String) {
    Div({
        style {
            flex(1)
            minWidth(150.px)
            padding(16.px)
            backgroundColor(Color.white)
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Color("#E0E0E0"))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        Div({
            style {
                fontSize(32.px)
            }
        }) {
            Text(icon)
        }

        Div({
            style {
                fontSize(24.px)
                fontWeight("bold")
                color(Color(color))
            }
        }) {
            Text(value)
        }

        Div({
            style {
                fontSize(12.px)
                color(Color("#666"))
            }
        }) {
            Text(title)
        }
    }
}

/**
 * Referral history list
 */
@Composable
fun ReferralHistory(history: List<ReferralItem>) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        H2({
            style {
                fontSize(20.px)
                color(Color("#333"))
                property("margin", "0")
            }
        }) {
            Text("Referral History")
        }

        if (history.isEmpty()) {
            EmptyHistoryView()
        } else {
            history.forEach { referral ->
                ReferralHistoryItem(referral)
            }
        }
    }
}

/**
 * Empty state for referral history
 */
@Composable
fun EmptyHistoryView() {
    Div({
        style {
            padding(48.px, 24.px)
            textAlign("center")
            backgroundColor(Color("#F9F9F9"))
            borderRadius(8.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                property("margin-bottom", "16px")
            }
        }) {
            Text("🎁")
        }

        P({
            style {
                fontSize(18.px)
                color(Color("#666"))
                property("margin", "0")
            }
        }) {
            Text("No referrals yet!")
        }

        P({
            style {
                fontSize(14.px)
                color(Color("#999"))
                property("margin-top", "8px")
            }
        }) {
            Text("Share your code above to start earning")
        }
    }
}

/**
 * Individual referral history item
 */
@Composable
fun ReferralHistoryItem(referral: ReferralItem) {
    Div({
        style {
            padding(16.px)
            backgroundColor(Color.white)
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Color("#E0E0E0"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            flexWrap(FlexWrap.Wrap)
            gap(12.px)
        }
    }) {
        // Left side - info
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
            }
        }) {
            Div({
                style {
                    fontSize(16.px)
                    fontWeight("600")
                    color(Color("#333"))
                }
            }) {
                Text("Referral #${referral.id.take(8)}")
            }

            Div({
                style {
                    fontSize(12.px)
                    color(Color("#666"))
                }
            }) {
                Text(formatDate(referral.createdAt))
            }
        }

        // Right side - status and amount
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                alignItems(AlignItems.Center)
            }
        }) {
            // Status badge
            StatusBadge(referral.status)

            // Amount (if completed)
            if (referral.status == ReferralStatus.COMPLETED) {
                Div({
                    style {
                        fontSize(16.px)
                        fontWeight("bold")
                        color(Color("#4CAF50"))
                    }
                }) {
                    Text("+₹${referral.rewardAmount.toInt()}")
                }
            }
        }
    }
}

/**
 * Status badge for referral items
 */
@Composable
fun StatusBadge(status: ReferralStatus) {
    val (text, bgColor, textColor) = when (status) {
        ReferralStatus.PENDING -> Triple("Pending", "#FFF3E0", "#F57C00")
        ReferralStatus.COMPLETED -> Triple("Completed", "#E8F5E9", "#2E7D32")
        ReferralStatus.FLAGGED -> Triple("Flagged", "#FFEBEE", "#C62828")
        ReferralStatus.RATE_LIMITED -> Triple("Rate Limited", "#F5F5F5", "#616161")
    }

    Span({
        style {
            padding(4.px, 12.px)
            backgroundColor(Color(bgColor))
            color(Color(textColor))
            borderRadius(12.px)
            fontSize(12.px)
            fontWeight("600")
        }
    }) {
        Text(text)
    }
}

// Helper functions

/**
 * Copy text to clipboard using Web Clipboard API
 */
fun copyToClipboard(text: String) {
    try {
        js("navigator.clipboard.writeText(text)")
        console.log("Copied to clipboard: $text")
    } catch (e: Exception) {
        console.error("Failed to copy to clipboard:", e)
        // Fallback: try older method
        try {
            val textArea = js("document.createElement('textarea')")
            textArea.value = text
            textArea.style.position = "fixed"
            textArea.style.top = "0"
            textArea.style.left = "0"
            js("document.body.appendChild(textArea)")
            textArea.focus()
            textArea.select()
            js("document.execCommand('copy')")
            js("document.body.removeChild(textArea)")
            console.log("Copied using fallback method")
        } catch (fallbackError: Exception) {
            console.error("Fallback copy also failed:", fallbackError)
        }
    }
}

/**
 * Share referral code via Web Share API with clipboard fallback
 */
fun shareReferralCode(code: String) {
    val message = """
        Hey! 👋
        
        Try Borshi for fresh, quality protein delivered to your door! 🥩🐟
        
        Use my code *$code* when signing up to get ₹100 off your first order.
        
        Sign up here: https://borshi.app
    """.trimIndent()

    // Use Web Share API if available, otherwise copy to clipboard
    val navigator = js("navigator")
    if (js("typeof navigator.share === 'function'") as Boolean) {
        val shareData = js("({})")
        shareData.title = "Join Borshi"
        shareData.text = message
        js("navigator.share(shareData)")
    } else {
        js("navigator.clipboard.writeText(message).catch(function(){});")
        console.log("Referral code copied to clipboard: $code")
    }
}

/**
 * Format timestamp to readable date
 */
fun formatDate(timestamp: Long): String {
    return try {
        val date = Date(timestamp.toDouble())
        val day = date.getDate()
        val month = date.getMonth() + 1
        val year = date.getFullYear()
        "$day/${month.toString().padStart(2, '0')}/$year"
    } catch (e: Exception) {
        "Unknown date"
    }
}

